import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Search, Plus, Pencil, Trash2 } from 'lucide-react';
import { getClinics, createClinic, updateClinic, deleteClinic } from '../../services/api';
import { toast } from 'sonner';

const validatePhone = (p) => /^05\d{9}$/.test(p);

export const AdminClinicsPanel = () => {
  const [clinics, setClinics] = useState([]);
  const [clinicSearch, setClinicSearch] = useState('');
  const [clinicModal, setClinicModal] = useState(null);
  const [clinicForm, setClinicForm] = useState({ name: '', phone: '', city: '', subscription_end: '', sms_credits: 0 });

  useEffect(() => {
    loadClinics();
  }, []);

  const loadClinics = async () => {
    try { const res = await getClinics(); setClinics(res.data); } catch {}
  };

  const openClinicEdit = (c) => {
    const subEnd = c.subscription_end ? c.subscription_end.split('T')[0] : '';
    setClinicForm({ name: c.name || '', phone: c.phone || '', city: c.city || '', subscription_end: subEnd, sms_credits: c.sms_credits || 0 });
    setClinicModal(c);
  };

  const handleSaveClinic = async () => {
    if (clinicForm.phone && !validatePhone(clinicForm.phone)) { toast.error('Telefon 05 ile başlamalı ve 11 haneli olmalı'); return; }
    try {
      if (clinicModal?.id) {
        await updateClinic(clinicModal.id, clinicForm);
        toast.success('Klinik güncellendi');
      } else {
        await createClinic(clinicForm);
        toast.success('Klinik oluşturuldu');
      }
      setClinicModal(null);
      loadClinics();
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteClinic = async (id) => {
    if (!window.confirm('Bu kliniği silmek istediğinize emin misiniz? Tüm kullanıcıları da silinecek.')) return;
    try { await deleteClinic(id); toast.success('Klinik silindi'); setClinics(p => p.filter(c => c.id !== id)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const filtered = clinics.filter(c => {
    if (!clinicSearch.trim()) return true;
    const q = clinicSearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.phone || '').includes(q) || (c.city || '').toLowerCase().includes(q);
  });

  return (
    <>
      <div className="space-y-1.5">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
            <input value={clinicSearch} onChange={e => setClinicSearch(e.target.value)} placeholder="Klinik veya telefon ara..."
              className="w-full h-10 bg-[#F2F2F7] rounded-xl pl-9 pr-3 text-sm outline-none" data-testid="clinic-search-input" />
          </div>
          <Button size="sm" onClick={() => { setClinicForm({ name: '', phone: '', city: '', subscription_end: '', sms_credits: 0 }); setClinicModal({}); }}
            className="bg-[#6b6de3] rounded-full text-xs" data-testid="add-clinic-btn">
            <Plus size={14} className="mr-1" /> Klinik Ekle
          </Button>
        </div>
        {filtered.map(c => (
          <div key={c.id} className="bg-white rounded-xl px-3 py-2 shadow-sm flex items-center gap-3" data-testid={`clinic-${c.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-gray-900 line-clamp-2 leading-snug">{c.name}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <p className="text-xs text-[#8E8E93]">{c.phone}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.subscription_end && c.subscription_end.split('T')[0] >= new Date().toISOString().split('T')[0] ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                  {c.subscription_end ? new Date(c.subscription_end.split('T')[0] + 'T12:00:00').toLocaleDateString('tr-TR') : 'Tanımsız'}
                </span>
                <span className="text-[10px] text-[#8E8E93]">SMS: <b className="text-gray-700">{c.sms_credits || 0}</b></span>
              </div>
            </div>
            <div className="flex gap-0.5 flex-shrink-0">
              <button onClick={() => openClinicEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#6b6de3]" data-testid={`edit-clinic-${c.id}`}><Pencil size={14} /></button>
              <button onClick={() => handleDeleteClinic(c.id)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#FF3B30]" data-testid={`del-clinic-${c.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Clinic Modal */}
      <Dialog open={!!clinicModal} onOpenChange={o => !o && setClinicModal(null)}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader><DialogTitle>{clinicModal?.id ? 'Klinik Düzenle' : 'Yeni Klinik'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Klinik Adı</Label><Input value={clinicForm.name} onChange={e => setClinicForm(p => ({ ...p, name: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">Telefon</Label><Input type="tel" maxLength={11} value={clinicForm.phone} onChange={e => setClinicForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="05XXXXXXXXX" className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">{'Şehir'}</Label><Input value={clinicForm.city} onChange={e => setClinicForm(p => ({ ...p, city: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            {clinicModal?.id && (
              <>
                <div><Label className="text-xs">Abonelik Bitiş Tarihi</Label><Input type="date" value={clinicForm.subscription_end} onChange={e => setClinicForm(p => ({ ...p, subscription_end: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
                <div><Label className="text-xs">SMS Kontör</Label><Input type="number" value={clinicForm.sms_credits} onChange={e => setClinicForm(p => ({ ...p, sms_credits: parseInt(e.target.value) || 0 }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
              </>
            )}
            <Button onClick={handleSaveClinic} className="w-full bg-[#6b6de3] rounded-full">{clinicModal?.id ? 'Güncelle' : 'Oluştur'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
